import React from 'react';
import { theme } from '../theme';
import type { SenderOrganisationRef } from '../serverTypes';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { pillStyle, INFO_PILL_BORDER_WIDTH } from './SecondaryAxisPills';
import { InfoTooltip } from './InfoTooltip';

// The amber "· prüfen" suffix colour — theme.colors.suggestion (harmonized, final review: this used
// to be its own hand-copied literal of the same hex) for the sender-identity registry's one review
// state. Ruling R14, 2026-09-13: the registry's own reviewer signal, not the K4-exclusive orange
// (#BC5A00) or the wim `pruefen` handling hue — a proposed MATCH is a different fact from a
// handling decision, and reusing either token would say the wrong thing on hover.
export const SENDER_TOKEN_PRUEFEN_COLOR = theme.colors.suggestion;

// THE gate every registry surface reads (final review, item 2) — one hook, so a surface can never
// disagree about whether this tenant has a sender registry at all. `sender_kind` is the served
// attribute the whole registry is keyed to: the admin tab that reviews the roster is gated on it
// (EinstellungenSeite's availableWhen), the filter section is gated on it
// (SenderOrganisationFilterSection), and the list row's SenderPill (below) is gated on it too — a
// ticket whose organisation carries a `kind` no vocabulary declares has no face to state. A backend
// that matched an organisation while serving no sender_kind is a half-deployed state, not a
// rendering decision, so every registry surface reads the same one condition rather than trusting
// whatever the ticket payload happens to carry. The DETAIL HEADER reads it again (part 7,
// 2026-10-11) for the same SenderPill; its address span stays unconditional — see TicketDetail.
export const useSenderRegistryServed = (): boolean => !!useCategoryMeta().attribute('sender_kind');

// ─── SenderPill ─────────────────────────────────────────
// <purpose>THE SENDER on a list row and in the detail header — ONE pill „Organisation · Art"
// (part 7, 2026-10-11, spec §16.3; supersedes the R33 partner pill AND the separate sender-kind
// attribute pill, which stated the same fact twice and, with the hover, three times). Row 3's
// opening word, in the registry's own token (theme.partnerPill). Where no organisation matched, the
// kind alone; where the kind's served face is null (privat/sonstige stay suppressed), the
// organisation alone; where there is neither, NO pill — never an empty pill, never a raw key.</purpose>
// <dependencies>theme.partnerPill.ink · pillStyle('info','row') for the pill METRICS only ·
// useCategoryMeta().attributeFace('sender_kind', …) for the kind word and .face('registry_status',
// 'proposed') for the hover's review word (both served — never typed here) · InfoTooltip for the
// full name + address.</dependencies>
// <constraints>THE BORDER STATES THE REGISTRY FACT, AND ONLY THAT (R33a/R33b, 2026-09-19, kept):
//   solid border  = a seeded or confirmed organisation — someone stands behind the name;
//   dashed border = a PROPOSAL (auto-created from a domain, unreviewed), a rejected row, or any
//                   status a later backend adds; its hover opens with „Vorgeschlagen – ungeprüft".
// A kind-only pill (no organisation) draws DASHED too: nothing in the registry stands behind it.
// The pill is always OUTLINED — plum ink on white, only the border STYLE moves — and the border
// rides INSIDE the box so solid/dashed is a repaint, never a relayout. A ticket's APPROVAL is stated
// once, by the decision badge beside it. The face is rendered verbatim (an organisation's name and
// a tenant's kind word are not words this product owns, so no propercasing).
// Layout-Stability: the pill takes the slot the kind pill held on the row's meta line and the
// organisation pill held on row 3; it adds no row. It is absent on a row with nothing to state and
// for a clerk who switched „Absender" off, and the only thing to its right on row 3 is a
// marginLeft:auto group, so a pill appearing pushes nothing sideways.</constraints>
export const SenderPill: React.FC<{
  organisation: SenderOrganisationRef | null;
  senderKind: string | null;
  email: string | null;
  /** Mount-site additions (e.g. the detail header's fade-in); merged last, like StufeChip's. */
  style?: React.CSSProperties;
}> = ({ organisation, senderKind, email, style }) => {
  const cm = useCategoryMeta();
  const ink = theme.partnerPill.ink;
  // The kind the REGISTRY row carries wins over the ticket's own stamp: it is the reviewed fact.
  const kind = organisation?.kind ?? senderKind;
  // null = the tenant serves this kind as suppressed (or the ticket states none): say no kind.
  const kindFace = kind ? cm.attributeFace('sender_kind', kind) : null;
  const orgFace = organisation ? (organisation.shortName ?? organisation.name) : null;
  const face = orgFace !== null ? (kindFace !== null ? `${orgFace} · ${kindFace}` : orgFace) : kindFace;
  if (face === null || face === '') return null;
  // „Established" rather than „confirmed": SEEDED rows were never reviewed by anyone and never need
  // to be, so the predicate is the two statuses that mean somebody stands behind the name.
  const established = !!organisation && (organisation.status === 'seeded' || organisation.status === 'confirmed');
  const borderStyle = established ? 'solid' : 'dashed';
  // part 7, 2026-10-11: data-registry is 'proposed' ONLY for a proposal. A rejected row is neither
  // established nor proposed -- it reads 'none' (the fourth state would add nothing a consumer acts
  // on). Core never serves a rejected organisation on a ticket today (SenderIdentityResolver: the
  // kind travels, the organisation does not); this arm covers a row rejected AFTER a ticket was stamped.
  const registry = organisation === null ? 'none' : established ? 'confirmed' : organisation.status === 'proposed' ? 'proposed' : 'none';
  const identity = organisation ? (email ? `${organisation.name} · ${email}` : organisation.name) : email;
  // The review word is the SERVED registry_status.proposed face; a null face (served as "say
  // nothing") drops the word and keeps the rest of the sentence.
  const proposedWord = cm.face('registry_status', 'proposed');
  // Only a PROPOSAL carries the served „Vorgeschlagen" word; any other unestablished status (a
  // rejected row) is merely „ungeprüft" -- it must never read as proposed.
  const unreviewed = organisation?.status === 'proposed' && proposedWord !== null ? `${proposedWord} – ungeprüft` : 'ungeprüft';
  const hover = organisation && !established ? [unreviewed, identity].join(' · ') : identity;
  const pill = (
    <span
      data-testid="sender-pill"
      // The state as DATA as well as paint: 'confirmed' (seeded reads as confirmed), 'proposed'
      // (every other status), 'none' (kind alone, no organisation).
      data-registry={registry}
      style={{
        ...pillStyle('info', 'row'),
        color: ink,
        backgroundColor: theme.colors.white,
        border: `${INFO_PILL_BORDER_WIDTH} ${borderStyle} ${ink}`,
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        ...style,
      }}
    >
      {face}
    </span>
  );
  return hover ? <InfoTooltip text={hover}>{pill}</InfoTooltip> : pill;
};
